/**
 * Home: the whole tenant, alive (ui/DESIGN.md §1). One sentence with the number of the hour, the map of
 * systems edge to edge with the documents moving as they really move, a glass feed of what just happened
 * over its right edge, and below the fold the integrations with their rhythm. The search field floats
 * over the top and is the only control. Everything comes from `activity` (polled every 15 s) plus two
 * small searches: the last hour's counts for the sentence, and the newest failed documents for the red
 * bars of the sparkline and the words of the incidents. One incident has one truth: the map's labels, the
 * feed's pinned line and the rhythm list all read the cloud's `activity.failing`; a cloud without it gets
 * the streaks derived here. A vault with nothing in the last day shows empty lanes and says what it holds from before.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { useEnv } from '../lib/envs.tsx';
import { Api, type Activity, type ActivityDoc } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { rememberSearch } from '../lib/hooks.ts';
import { useNow } from '../lib/asof.tsx';
import { age } from '../lib/freshness.ts';
import { stripSystem } from '../lib/systems.ts';
import { Shell } from '../shell/Shell.tsx';
import { Glyph, Kicker, Surface, cx } from '../design/primitives.tsx';
import { Map as SystemsMap } from '../map/Map.tsx';
import { fromActivity, noun } from '../map/adapters.ts';
import { FAMILY_PREFIX, simplifyMap } from '../map/simplify.ts';
import { systemName } from '../lib/systems.ts';
import { FacetChip, Segmented } from '../components/FilterChips.tsx';
import { departuresOf, keepDepartures, readPace, storedPace, withPace, type Departure, type Pace, type Seen } from '../now/departures.ts';
import type { MapFlow } from '../map/types.ts';
import { Peek, PeekLayout, usePeek } from '../peek/Peek.tsx';
import { Sentence } from '../now/Sentence.tsx';
import type { History } from '../now/emptyState.ts';
import { FeedDock, FeedTicker, useFeedPin } from '../now/Feed.tsx';
import { Rhythms } from '../now/Rhythms.tsx';
import { GroupFilter } from '../components/GroupFilter.tsx';
import { NameToggle } from '../systems/parts.tsx';
import { filterActivity, hiddenSystems, readHidden, withoutSystems } from '../lib/groups.ts';
import { useSystems } from '../lib/systemsdata.tsx';
import { useSearchParams } from '../lib/liveparams.ts';
import { feedLines, storedAs, voice, type FeedAbout, type FeedLine } from '../now/feedLines.ts';
import { failingOf, hourlyBars, rhythmRows } from '../now/model.ts';
import { useClock, useLive } from '../now/useLive.ts';
import '../now/now.css';

const POLL_MS = 15_000;
/** The newest failed documents asked for: the words of every incident, and enough to tell a streak without the cloud's. */
const FAILED_SIZE = '40';
const SEEN_KEY = 'mv.now.seen';
/** First visit only: remembered in localStorage (which may be blocked, so every touch is guarded). */
function firstVisit(): boolean {
  try { return localStorage.getItem(SEEN_KEY) === null; } catch { return false; }
}

export function Now() {
  const { tenant } = useAuth();
  const tid = tenant?.id ?? '';
  const navigate = useNavigate();
  const { link, current } = useEnv();
  const peek = usePeek();
  // Ages and freshness follow a slow clock; the live time has its own, in the sentence
  useClock(30_000);
  const now = useNow();

  // The groups of systems hidden (`hide=`): left out of the map, the feed, the integrations and the counts
  const [params, setParams] = useSearchParams();
  // The feed pinned open on a wide screen, or folded to a tab that opens while pointed at (remembered by the browser)
  const [feedPinned, toggleFeedPin] = useFeedPin();
  const { systems } = useSystems();
  const hiddenKey = readHidden(params).join('|');
  const hidden = useMemo(() => hiddenSystems(systems.values(), hiddenKey ? hiddenKey.split('|') : []), [systems, hiddenKey]);
  const live = useLive(() => Promise.all([
    Api.activity(tid, new URLSearchParams({ from: 'now-24h' })),
    Api.search(tid, new URLSearchParams({ q: withoutSystems('', hidden), size: '0', from: 'now-1h' })),
    Api.search(tid, new URLSearchParams({ q: withoutSystems('@status=FAILED', hidden), size: FAILED_SIZE, from: 'now-24h' })),
  ]).then(([activity, hour, failed]) => ({ activity, hour, failed })), POLL_MS, [tid, [...hidden].sort().join(',')]);
  const data = live.data;
  const empty = !!data && data.activity.total === 0;

  // An empty last day: is there older data? Asked once per empty load (not on every poll), only when empty
  const [past, setPast] = useState<{ key: string; h: History } | null>(null);
  const pastKey = `${tid}|${current?.id ?? ''}`;
  useEffect(() => {
    if (!empty) return;
    let cancelled = false;
    Api.search(tid, new URLSearchParams({ q: '', size: '1', from: '2000-01-01T00:00:00Z' }))
      .then((r) => { if (!cancelled) setPast({ key: pastKey, h: { newest: r.hits[0]?.timestamp ?? null, total: r.total, cls: r.hits[0]?.messageClass } }); })
      .catch(() => { if (!cancelled) setPast({ key: pastKey, h: { newest: null, total: 0 } }); });
    return () => { cancelled = true; };
  }, [empty, pastKey, tid]);
  const history = past && past.key === pastKey ? past.h : null;

  // The one truth about what is failing: the cloud's streaks; an older cloud without them gets the same
  // shape derived from the newest failed documents against the runs' starts
  const activity: Activity | null = useMemo(() => {
    if (!data) return null;
    const a = data.activity.failing ? data.activity : { ...data.activity, failing: failingOf(data.activity.flows, data.failed.hits, now) };
    return filterActivity(a, hidden);
  }, [data, now, hidden]);
  // A busy map made readable (map/simplify.ts): a hub's steps as one node, copies of a process per system as a family;
  // a family opened (`open=family:…`, a history step) is shown as it is
  const openKey = params.getAll('open').join('|');
  const opened = useMemo(() => new Set(openKey ? openKey.split('|') : []), [openKey]);
  const map = useMemo(() => {
    if (!activity || empty) return null;
    const raw = fromActivity(activity, now);
    // An overdue integration's document stands still
    const silent = new Set(activity.silent.map((x) => x.process));
    for (const f of raw.flows) if (silent.has(f.process)) f.quiet = true;
    return simplifyMap(raw.nodes, raw.flows, opened, systemName);
  }, [activity, empty, now, opened]);

  // How the documents move (`pace=`, a history step, remembered): live — a document when a run happens — or the rhythm
  // of each integration (a lap, then a pause as long as it usually waits)
  const [pacePref] = useState(storedPace);
  const pace = readPace(params, pacePref);
  const choosePace = (v: Pace) => {
    if (v === pace) return;
    const here = new URL(window.location.href);
    if (here.searchParams.get('pace') !== pace) {
      here.searchParams.set('pace', pace);
      window.history.replaceState(window.history.state, '', here.pathname + here.search + here.hash);
    }
    setParams((p) => withPace(p, v));
  };
  // Live: the runs that happened since the last poll, per process, kept a couple of minutes
  const seenRuns = useRef(new Map<string, Seen>());
  const [departed, setDeparted] = useState(new Map<string, Departure[]>());
  useEffect(() => {
    if (!activity) return;
    const at = Date.now();
    const r = departuresOf(seenRuns.current, activity.flows, at);
    seenRuns.current = r.seen;
    setDeparted((kept) => keepDepartures(kept, r.departed, at));
  }, [activity]);
  const departures = useMemo(() => {
    const out = new Map<string, Departure[]>();
    for (const f of map?.flows ?? []) { const d = departed.get(f.process); if (d) out.set(f.id, d); }
    return out;
  }, [map, departed]);
  const setOpen = (key: string, on: boolean) => setParams((p) => {
    const next = new URLSearchParams(p);
    const keep = next.getAll('open').filter((k) => k !== key);
    next.delete('open');
    for (const k of on ? [...keep, key] : keep) next.append('open', k);
    return next;
  });
  const openNode = (id: string) => {
    if (id.startsWith(FAMILY_PREFIX)) { setOpen(id, true); return; }
    const members = map?.nodes.find((n) => n.id === id)?.members;
    const q = members && members.length > 1 ? members.map((m) => `@class=${m}`).join(' OR ') : `@class=${id}`;
    void navigate(link(`/s?${new URLSearchParams({ q })}`));
  };
  const lines = useMemo(() => (data && activity && !empty ? feedLines({ activity, failed: data.failed.hits, now }) : []), [activity, data, empty, now]);
  const rhythms = useMemo(() => (activity && !empty ? rhythmRows(activity, now) : []), [activity, empty, now]);
  const bars = useMemo(() => (data ? hourlyBars(data.activity.histogram, data.failed.histogram, now) : []), [data, now]);
  const movedHour = data?.hour.total ?? null;
  // One number for the hour's failures whatever the cloud's facets say: never less than the current hour's failed bar
  const failedHour = data ? Math.max(data.hour.facets.status?.find((s) => s.value === 'FAILED')?.count ?? 0, bars[bars.length - 1]?.failed ?? 0) : null;
  const [cue] = useState(firstVisit);
  useEffect(() => {
    if (!cue || !data || empty) return;
    const t = window.setTimeout(() => { try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* blocked */ } }, 9000);
    return () => window.clearTimeout(t);
  }, [cue, data, empty]);

  // The map takes what is left of the viewport under the sentence, so every lane fits without scrolling;
  // measured again when the window or the sentence block changes size (a peek narrowing the column wraps it)
  const head = useRef<HTMLDivElement>(null);
  const mapBox = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(720);
  useLayoutEffect(() => {
    const measure = () => {
      const el = mapBox.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setMaxHeight(Math.max(360, window.innerHeight - top - 8));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = head.current ? new ResizeObserver(measure) : null;
    if (head.current) ro?.observe(head.current);
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [live.loading]);
  const layout = useMemo(() => ({ maxHeight }), [maxHeight]);
  // When the lanes need more than the viewport gives (many systems), the map scrolls inside its box, and a
  // fade at the bottom says there is more, rather than a lane being cut off
  const [moreBelow, setMoreBelow] = useState(false);
  const checkMore = useCallback(() => {
    const el = mapBox.current;
    if (el) setMoreBelow(el.scrollHeight - el.clientHeight - el.scrollTop > 2);
  }, []);
  useLayoutEffect(() => {
    checkMore();
    const el = mapBox.current;
    const ro = el?.firstElementChild ? new ResizeObserver(checkMore) : null;
    if (el?.firstElementChild) ro?.observe(el.firstElementChild);
    return () => ro?.disconnect();
  }, [checkMore, map, maxHeight]);

  const [sample, setSample] = useState<{ flow: MapFlow; at: { x: number; y: number } } | null>(null);
  const sampleDoc = useMemo(() => {
    if (!sample || !data) return null;
    const mine = data.activity.latest.filter((d) => d.process === sample.flow.process);
    return mine.find((d) => d.cls === (sample.flow.to ?? sample.flow.from)) ?? mine[0] ?? null;
  }, [sample, data]);

  const go = (q: string) => {
    if (!q) return;
    rememberSearch(q);
    void navigate(link(`/s?${new URLSearchParams({ q })}`));
  };
  const openLine = (line: FeedLine) => {
    if (line.open.kind === 'document') peek.open({ kind: 'document', ref: line.open.ref });
    else void navigate(link(`/s?${new URLSearchParams({ q: line.open.q })}`));
  };
  // A lane's system and the integration a feed line names open as peeks (the Systems view is a click further)
  const openThing = (about: FeedAbout) => peek.open({ kind: 'integration', process: about.process });
  const openProcess = (process: string) => void navigate(link(`/s?${new URLSearchParams({ q: `@process="${process}"` })}`));
  const time = useMemo(() => new URLSearchParams({ from: 'now-24h' }), []);

  return (
    <Shell search={{ value: '', onSubmit: go, size: 'hero' }}>
      <PeekLayout open={peek.stack.length > 0} peek={<Peek time={time} />}>
        <div className="now" data-feed={feedPinned ? 'pinned' : 'auto'}>
          <div className="relative">
            <div ref={head} className="now-headwrap w-full px-6 pb-2">
              <Sentence moved={movedHour} failed={failedHour} bars={bars} empty={empty} loading={live.loading}
                history={history} now={now} envName={current?.name ?? null}
                onLook={() => void navigate(link(`/s?${new URLSearchParams({ q: history?.cls ? `@class=${history.cls}` : '', range: 'all' })}`))} />
              <div className="flex items-center gap-x-6 gap-y-2 flex-wrap mt-2">
                <GroupFilter />
                <NameToggle />
                <Segmented label="Motion" ariaLabel="How the documents move" value={pace} onChange={choosePace}
                  options={[['live', 'Live'], ['rhythm', 'Rhythm']] as const} />
                {[...opened].filter((k) => k.startsWith(FAMILY_PREFIX)).map((k) => (
                  <FacetChip key={k} chosen onClick={() => setOpen(k, false)} title="Show the copies as one again" data-key={k}>
                    {k.slice(FAMILY_PREFIX.length)} opened ✕
                  </FacetChip>
                ))}
                {!empty && map && <span className="mvm-window" aria-hidden="true">counts: today</span>}
              </div>
              {live.error && !data && <p className="m-0 mt-3 text-small text-fail">{live.error}</p>}
            </div>
            {!empty && (
              <div className="now-ticker-slot w-full px-6 pb-6">
                <FeedTicker lines={lines} now={now} loading={live.loading} where={tenant?.name ?? ''} onOpen={openLine} onThing={openThing} />
              </div>
            )}
            <div ref={mapBox} className={cx('now-map', empty && 'now-map-empty')} style={{ maxHeight }} data-more={moreBelow ? '' : undefined} onScroll={checkMore}>
              {map ? (
                <SystemsMap nodes={map.nodes} flows={map.flows} layout={layout} now={now} motion="live" speed={1} glyphScale={1} cue={cue}
                  pace={pace} departures={departures}
                  laneLabels={map.lanes}
                  onNode={openNode}
                  onFlow={(f) => openProcess(f.process)}
                  onLane={(system) => peek.open({ kind: 'system', code: system })}
                  onGlyphHover={(flow, at) => setSample(flow && at ? { flow, at } : null)} />
              ) : <MapSkeleton height={maxHeight} still={empty} />}
            </div>
            <div className="now-feed-slot">
              <div className="w-full relative h-full">
                <FeedDock pinned={feedPinned} onPin={toggleFeedPin} lines={lines} now={now} loading={live.loading} where={tenant?.name ?? ''} onOpen={openLine} onThing={openThing} />
              </div>
            </div>
          </div>
          <Rhythms rows={rhythms} now={now} onRow={openProcess} />
        </div>
      </PeekLayout>
      {sample && <SampleCard flow={sample.flow} at={sample.at} doc={sampleDoc} now={now} />}
    </Shell>
  );
}

/** The lanes before the data: the same stripes, waiting. */
function MapSkeleton({ height, still }: { height: number; still?: boolean }) {
  return (
    <div className="flex flex-col" style={{ height }} aria-hidden="true">
      {Array.from({ length: 10 }, (_, i) => (
        <div key={i} className={cx('flex-1 flex items-center pl-4', i % 2 ? 'bg-n2/40' : 'bg-transparent')}>
          {still ? <span className="rounded-full size-6 border border-dashed border-line-strong" /> : <span className="mv-shimmer rounded-full size-6 opacity-70" />}
        </div>
      ))}
    </div>
  );
}

/** Under a moving document: the newest real one of that integration, as the activity knows it. */
function SampleCard({ flow, at, doc, now }: { flow: MapFlow; at: { x: number; y: number }; doc: ActivityDoc | null; now: number }) {
  return (
    <Surface level={2} className="now-sample p-3 mv-pop" style={{ left: at.x, top: at.y }} data-hold-map="">
      <Kicker className="truncate">{stripSystem(flow.process)}</Kicker>
      {doc ? (
        <div className="flex items-start gap-2.5 mt-1.5 min-w-0">
          <Glyph format={doc.type} size={24} failed={doc.status === 'FAILED'} className="mt-0.5" />
          <div className="min-w-0">
            <div className="text-small text-n10 leading-snug">{doc.info ? voice(doc.info, doc.status, storedAs(doc, flow.process)) : `${noun(doc.cls)} ${doc.type ?? ''}`.trim()}</div>
            <div className="font-mono text-micro text-n7 mt-1">{dayjs(doc.at).format('HH:mm:ss')} · {age(doc.at, now)} ago</div>
          </div>
        </div>
      ) : (
        <div className="text-small text-n8 mt-1">{flow.label}</div>
      )}
    </Surface>
  );
}
